import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Send, FileText, History, CheckSquare,
  BarChart3, QrCode, Settings as SettingsIcon, Upload, LogOut, Menu, X, Bell, Stethoscope, MessageSquareText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, key: "dashboard" },
  { to: "/clientes", label: "Clientes", icon: Users, key: "clientes" },
  { to: "/importar", label: "Importar Contatos", icon: Upload, key: "importar" },
  { to: "/disparos", label: "Disparos", icon: Send, key: "disparos" },
  { to: "/envio-manual", label: "Envio Manual", icon: MessageSquareText, key: "envio-manual" },
  { to: "/modelos", label: "Modelos", icon: FileText, key: "modelos" },
  { to: "/historico", label: "Histórico", icon: History, key: "historico" },
  { to: "/followups", label: "Follow-ups", icon: CheckSquare, key: "followups" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, key: "relatorios" },
  { to: "/whatsapp", label: "WhatsApp", icon: QrCode, key: "whatsapp" },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, key: "configuracoes" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-transparent">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky top-0 z-50 h-screen w-72 flex flex-col transition-transform duration-300 lg:translate-x-0",
          "bg-gradient-to-b from-emerald-950 via-[#07231d] to-slate-950 text-slate-300 border-r border-white/5",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-3 px-6 h-16 border-b border-white/5">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
            <Stethoscope className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-display font-extrabold text-white text-lg leading-none tracking-tight">ZAP DOUTOR</p>
            <p className="text-[10px] text-emerald-400/70 mt-1 uppercase tracking-[0.2em]">Gestão Clínica</p>
          </div>
          <button className="ml-auto lg:hidden text-slate-400" onClick={() => setOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200",
                  isActive
                    ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                )
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors"
          >
            <LogOut className="h-[18px] w-[18px]" /> Sair
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/70 backdrop-blur-xl border-b border-emerald-900/10 sticky top-0 z-30 flex items-center gap-4 px-4 sm:px-6 shadow-soft">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <div className="hidden sm:block">
            <p className="text-[10px] text-emerald-600/70 uppercase tracking-[0.2em]">Clínica</p>
            <p className="text-sm font-semibold text-slate-800 -mt-0.5 font-display">ZAP DOUTOR</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button className="relative p-2 rounded-xl hover:bg-emerald-50 transition-colors" data-testid="notifications-button">
              <Bell className="h-5 w-5 text-slate-600" />
              <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-emerald-500 rounded-full ring-2 ring-white" />
            </button>
            <div className="flex items-center gap-3 pl-3 border-l border-slate-200">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center text-sm font-semibold shadow-md shadow-emerald-500/30">
                {initials(user?.name)}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-slate-800 leading-none">{user?.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">Administrador</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
